import { TICK_MS, ticks, wholeTicks } from "./constants";

/** Snapshot loss from gaps in the tick sequence, over a window of roughly the last few seconds of snapshots. */
export class LossMeter {
  private received = 0;
  private lost = 0;
  private lastTick = -1;

  /** `window`: snapshots (one per tick) the counts are halved after; 5 s by default. */
  constructor(private readonly window: number = wholeTicks(5)) {}

  /** Records the tick of an arriving snapshot. Duplicates and late (out-of-order) ones are ignored. */
  record(tick: number): void {
    if (this.lastTick >= 0) {
      if (tick <= this.lastTick) return;
      this.lost += tick - this.lastTick - 1;
    }
    this.lastTick = tick;
    this.received++;
    if (this.received + this.lost > this.window) {
      this.received /= 2;
      this.lost /= 2;
    }
  }

  /** 0..100. */
  percent(): number {
    const total = this.received + this.lost;
    return total > 0 ? (100 * this.lost) / total : 0;
  }

  reset(): void {
    this.received = 0;
    this.lost = 0;
    this.lastTick = -1;
  }
}

/** Bytes per second over a sliding one-second window (four 250 ms buckets). */
export class RateMeter {
  private static readonly BUCKET_MS = 250;
  private static readonly BUCKETS = 4;
  private readonly bytes = new Float64Array(RateMeter.BUCKETS);
  private bucketIndex = -1;

  private advance(nowMs: number): number {
    const idx = Math.floor(nowMs / RateMeter.BUCKET_MS);
    if (this.bucketIndex < 0) {
      this.bucketIndex = idx;
    } else if (idx - this.bucketIndex >= RateMeter.BUCKETS) {
      this.bytes.fill(0);
      this.bucketIndex = idx;
    } else {
      while (this.bucketIndex < idx) {
        this.bucketIndex++;
        this.bytes[this.bucketIndex % RateMeter.BUCKETS] = 0;
      }
    }
    return idx;
  }

  add(count: number, nowMs: number): void {
    const idx = this.advance(nowMs);
    this.bytes[idx % RateMeter.BUCKETS] += count;
  }

  /** Kilobits per second (1000 bits = 1 kb). */
  kbps(nowMs: number): number {
    this.advance(nowMs);
    let sum = 0;
    for (let i = 0; i < RateMeter.BUCKETS; i++) sum += this.bytes[i];
    return (sum * 8) / 1000 / ((RateMeter.BUCKETS * RateMeter.BUCKET_MS) / 1000);
  }
}

/** Counts events in the last minute. */
export class PerMinuteCounter {
  private readonly times: number[] = [];

  record(nowMs: number): void {
    this.times.push(nowMs);
    this.trim(nowMs);
  }

  perMinute(nowMs: number): number {
    this.trim(nowMs);
    return this.times.length;
  }

  private trim(nowMs: number): void {
    let drop = 0;
    while (drop < this.times.length && nowMs - this.times[drop] > 60_000) drop++;
    if (drop > 0) this.times.splice(0, drop);
  }
}

/**
 * Round trip time and server clock from ping/pong samples (docs/NETCODE.md "Clock and tick
 * synchronization"). A sample is the time until the pong handler ran, so anything that delays the
 * handler or the ping (a blocked main thread, a busy datagram writer) only ever adds to it. RTT is
 * therefore the lowest of the last few samples, jitter the median deviation of those samples from
 * it (one stalled pong is an outlier, sustained variation is not), and the server tick estimate
 * uses the lowest-RTT sample too, advanced at the measured clock rate: the server's tick counter
 * and the browser's clock do not run at the same speed (a WSL2 guest's monotonic clock ran 4.2 %
 * fast against Windows), and a two-second-old sample would be ten ticks (84 ms) off at that rate.
 */
export class ConnectionClock {
  private static readonly KEEP = 8;
  /** Samples kept for the clock rate (8 seconds of pongs). */
  private static readonly RATE_KEEP = 32;
  /** Samples and time span (local ticks; 3 s) the rate needs. */
  private static readonly RATE_MIN_SAMPLES = 8;
  private static readonly RATE_MIN_SPAN_TICKS = ticks(3);
  /** Samples whose RTT is within this many ms of the best count for the rate. */
  private static readonly RATE_RTT_SLACK_MS = 4;
  /** Samples needed before the jitter means anything. */
  private static readonly MIN_JITTER_SAMPLES = 3;
  private rtt = 0;
  private jitter = 0;
  private samples = 0;
  /** Recent samples: the server tick (fraction) and the local time (in ticks) it was valid at, and the RTT. */
  private readonly recent: { server: number; local: number; rtt: number }[] = [];
  private readonly long: { server: number; local: number; rtt: number }[] = [];
  private readonly deviations: number[] = [];
  private rateValue: number | null = null;

  /** `clientTimeMs`: the echoed ping time; `serverTick`/`tickPhase`: the server clock when it answered. */
  onPong(nowMs: number, clientTimeMs: number, serverTick: number, tickPhase: number): void {
    const sample = nowMs - clientTimeMs;
    if (!(sample >= 0) || !Number.isFinite(sample)) return;
    this.samples++;
    // The server answered about half a round trip ago.
    const serverTickNow = serverTick + tickPhase / 256 + sample / 2 / TICK_MS;
    const row = { server: serverTickNow, local: nowMs / TICK_MS, rtt: sample };
    this.recent.push(row);
    if (this.recent.length > ConnectionClock.KEEP) this.recent.shift();
    this.long.push(row);
    if (this.long.length > ConnectionClock.RATE_KEEP) this.long.shift();
    this.updateEstimates();
  }

  private updateEstimates(): void {
    let lowest = Infinity;
    for (const o of this.recent) if (o.rtt < lowest) lowest = o.rtt;
    this.rtt = lowest;
    const deviations = this.deviations;
    deviations.length = 0;
    for (const o of this.recent) deviations.push(o.rtt - lowest);
    if (deviations.length < ConnectionClock.MIN_JITTER_SAMPLES) {
      this.jitter = 0;
    } else {
      deviations.sort((a, b) => a - b);
      this.jitter = deviations[deviations.length >> 1] as number;
    }
    this.rateValue = this.fitRate();
  }

  /** Least squares slope of the server tick against local ticks over the samples with a near-best RTT. */
  private fitRate(): number | null {
    const long = this.long;
    if (long.length < ConnectionClock.RATE_MIN_SAMPLES) return null;
    let best = Infinity;
    for (const o of long) if (o.rtt < best) best = o.rtt;
    const limit = best + ConnectionClock.RATE_RTT_SLACK_MS;
    let n = 0;
    let sx = 0;
    let sy = 0;
    let first = Infinity;
    let last = -Infinity;
    for (const o of long) {
      if (o.rtt > limit) continue;
      n++;
      sx += o.local;
      sy += o.server;
      if (o.local < first) first = o.local;
      if (o.local > last) last = o.local;
    }
    if (n < ConnectionClock.RATE_MIN_SAMPLES || last - first < ConnectionClock.RATE_MIN_SPAN_TICKS) return null;
    const mx = sx / n;
    const my = sy / n;
    let sxx = 0;
    let sxy = 0;
    for (const o of long) {
      if (o.rtt > limit) continue;
      sxx += (o.local - mx) * (o.local - mx);
      sxy += (o.local - mx) * (o.server - my);
    }
    if (sxx === 0) return null;
    const slope = sxy / sxx;
    // Beyond +-15 % the pongs are noise, not a clock.
    return slope > 0.85 && slope < 1.15 ? slope : null;
  }

  /** True once a pong has been received. */
  ready(): boolean {
    return this.samples > 0;
  }

  rttMs(): number {
    return this.rtt;
  }

  jitterMs(): number {
    return this.jitter;
  }

  /**
   * Server ticks per local tick (1.0417: the server runs 4.17 % fast against this browser's clock);
   * null until the pongs of a few seconds say.
   */
  rate(): number | null {
    return this.rateValue;
  }

  /** Fractional server tick at local time `nowMs` (0 before the first pong). */
  estServerTick(nowMs: number): number {
    if (this.recent.length === 0) return 0;
    let best = this.recent[0] as { server: number; local: number; rtt: number };
    for (const o of this.recent) if (o.rtt < best.rtt) best = o;
    return best.server + (this.rateValue ?? 1) * (nowMs / TICK_MS - best.local);
  }

  /** Tick the first predicted input should be for: `est_server_tick + ceil(rtt/2/dt) + target_lead` (`targetLead`: `TickClock.targetLead()`). */
  initialPredTick(nowMs: number, targetLead: number): number {
    return Math.ceil(this.estServerTick(nowMs) + this.rtt / 2 / TICK_MS + targetLead);
  }
}
